import { memo, useRef, useState } from 'react'
import clsx from 'clsx'

const ArrowIcon = ({ className = '' }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.41058 6.91074C4.73602 6.58531 5.26366 6.58531 5.58909 6.91074L9.99984 11.3215L14.4106 6.91074C14.736 6.58531 15.2637 6.58531 15.5891 6.91074C15.9145 7.23618 15.9145 7.76382 15.5891 8.08925L10.5891 13.0893C10.2637 13.4147 9.73602 13.4147 9.41058 13.0893L4.41058 8.08925C4.08514 7.76382 4.08514 7.23618 4.41058 6.91074Z"
        fill="currentColor"
      />
    </svg>
  )
}

const BULLET_STYLE = `relative pl-12 before:absolute before:top-2 before:left-0 before:h-[5px] before:w-[5px] before:rounded-full before:bg-m-gray-300 before:content-['']`

export const MStockFooter = memo(({ exchanges }) => {
  const [isOpen, setIsOpen] = useState(false)
  const titleRef = useRef(null)

  const handleToggle = () => {
    setIsOpen((prev) => !prev)
  }

  const handleTransitionEnd = (event) => {
    if (event.target !== event.currentTarget) return
    if (event.propertyName !== 'grid-template-rows') return
    if (!isOpen) return

    titleRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  return (
    <footer className="x-spacing bg-m-surface-subtle py-24">
      <button
        ref={titleRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls="footer-notice"
        className="text-m-content-primary text-m-body-semibold flex min-h-[20px] w-full touch-manipulation items-center justify-between"
        onClick={handleToggle}
      >
        유의사항
        <ArrowIcon
          className={clsx(
            'text-m-content-secondary transition-transform duration-300 ease-[cubic-bezier(0.8,0,0.2,1)] motion-reduce:transition-none',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      <div
        id="footer-notice"
        inert={!isOpen}
        onTransitionEnd={handleTransitionEnd}
        className={clsx(
          'grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.8,0,0.2,1)] motion-reduce:transition-none',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="min-h-0 transform-gpu overflow-hidden">
          <ul className="text-m-body-sm text-m-content-secondary mt-16 space-y-16">
            <li className={BULLET_STYLE}>
              본 화면의 디지털자산 시세 및 투자정보는 보난자랩에서 제공하며, 시세는 국내 5대
              거래소의 거래량을 가중평균해 산출한 값을 제공합니다.
            </li>

            <li className={BULLET_STYLE}>
              디지털자산 분류체계 K-DACS는 국내 디지털자산 거래소에서 거래 지원 중인 디지털자산의
              정량·정성 데이터를 종합적으로 분석해 마련된 표준화 된 분류 체계로, 보난자랩과
              에프앤가이드가 공동개발한 지표입니다.
            </li>

            <li className={BULLET_STYLE}>
              디지털자산은 24시간 연중무휴로 거래되며, 일간 차트와 등락률은 매일 오전 9시(KST)를
              기준으로 해당 시점부터의 가격 흐름과 가격 변동률을 보여줍니다.
            </li>

            <li className={BULLET_STYLE}>
              본 화면에서 제공하는 투자정보는 투자판단을 위한 참고자료이며, 특정 디지털자산의 매매를
              권유하거나 투자 성과를 보장하지 않습니다.
            </li>

            <li className={BULLET_STYLE}>
              당사는 디지털자산 매매 서비스를 제공하지 않으며, 본 화면의 콘텐츠는 디지털자산
              시황·기술 등에 관한 단순 정보 제공을 목적으로 합니다.
            </li>

            <li className={BULLET_STYLE}>
              데이터 제공처 및 집계 시점 등에 따라 실제 시장 정보와 차이가 발생할 수 있으며, 정보의
              정확성이나 완전성을 보장하지 않습니다.
            </li>

            <li className={BULLET_STYLE}>
              제휴사가 제공하는 콘텐츠의 저작권은 해당 제휴사에 있으며, 사전 동의 없이
              복제·배포·전송·변형 등의 방법으로 이용할 수 없습니다.
            </li>

            <li className={BULLET_STYLE}>
              개별 거래소의 실제 시세는 각 거래소 웹사이트 또는 앱을 통해 확인하시기 바랍니다.
              <span className="inline space-x-4">
                (
                {exchanges.map(({ key, label, link }, index) => (
                  <a key={key} href={link} target="_blank" rel="noopener noreferrer">
                    <span className="inline-block border-b pb-0 leading-none">{label}</span>
                    {exchanges.length !== index + 1 && <span>,</span>}
                  </a>
                ))}
                )
              </span>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  )
})
